// Which tables a query reads.
//
// THIS IS A SECURITY BOUNDARY, NOT A CONVENIENCE.
//
// A saved view carries `tables[]`, and the anonymous rows route at the edge
// serves a table only when the view's list names it. That check is worth having
// only because this function derives the list from the query's own AST — if a
// person could type the list, the check would be checking the person.
//
// So the rule here is: report every table the query can reach, and REFUSE
// anything whose shape is not understood. Reporting too many is a view that
// loads a table it did not need. Reporting too few is a shared link that reads a
// table nobody authorised.
//
// WHAT THE ARCHIVE'S VERSION MISSED, MEASURED ON ALASQL 4.19.0
//
// stack-pkg/src/libs/site-view/index.js walked `parsed.from[].tableid` and
// `parsed.joins[].table.tableid` and nothing else. Four shapes escape that:
//
//   SELECT * FROM a UNION SELECT * FROM secret
//       the second statement hangs off `st.union` — a whole select node the walk
//       never visits. `secret` is invisible, and on a shared view that is a read
//       of a table the allow-list never authorised.
//
//   WITH c AS (SELECT * FROM base) SELECT * FROM c
//       `withs[]` holds the CTE bodies and the OUTER query moves to `st.select`,
//       so `st.from` is empty and the walk reports nothing at all. `c` is also
//       not a table — it is a name bound by the query — so reporting it would be
//       as wrong as missing `base`.
//
//   SELECT * FROM (SELECT * FROM inner_t) x
//       a subquery in FROM has no `tableid` at all, so the archive pushed
//       `undefined` into the list.
//
//   SELECT * FROM a WHERE id IN (SELECT id FROM sub)
//       alasql collects these into `st.queries`, which the walk never read.
//
// Every one of them is covered below and pinned in the test beside it.

// The set operators, each of which carries a complete select node of its own.
const SET_OPS = ["union", "unionall", "except", "intersect"];

const isSelectNode = (node) =>
  node
  && typeof node === "object"
  && (Array.isArray(node.from) || Array.isArray(node.columns) || Array.isArray(node.withs));

// `bound` is the set of names the query binds for itself — CTE names, and the
// aliases of subqueries in FROM. They look exactly like table ids and are not
// tables, so they are subtracted rather than reported.
const walkSelect = (node, out, bound) => {
  if (!isSelectNode(node)) {
    throw new Error("sql.ast.unrecognised");
  }

  if (Array.isArray(node.withs)) {
    for (const w of node.withs) {
      if (!w || typeof w.name !== "string" || !w.select) {
        throw new Error("sql.ast.with.unrecognised");
      }
      // The body is walked BEFORE the name is bound, so `WITH a AS (SELECT *
      // FROM a)` still reports the real `a` it reads.
      walkSelect(w.select, out, bound);
      bound.add(w.name);
    }
  }

  // With a WITH present the outer query moves here.
  if (node.select) {
    walkSelect(node.select, out, bound);
  }

  for (const t of node.from || []) {
    if (!t || typeof t !== "object") {
      throw new Error("sql.ast.from.unrecognised");
    }
    if (typeof t.tableid === "string") {
      if (!bound.has(t.tableid)) out.add(t.tableid);
    } else if (isSelectNode(t)) {
      walkSelect(t, out, bound);
    } else {
      // A table-valued function, a variable, a parameter — anything that names
      // a source this platform does not have. Refused rather than skipped: a
      // shape that reaches here is one nobody has thought about, and skipping it
      // silently is how the allow-list gets a hole.
      throw new Error("sql.ast.from.unsupported");
    }
  }

  for (const j of node.joins || []) {
    if (!j || typeof j !== "object") {
      throw new Error("sql.ast.join.unrecognised");
    }
    const table = j.table || j;
    if (typeof table.tableid === "string") {
      if (!bound.has(table.tableid)) out.add(table.tableid);
    } else if (isSelectNode(table)) {
      walkSelect(table, out, bound);
    } else if (isSelectNode(j.select)) {
      walkSelect(j.select, out, bound);
    } else {
      throw new Error("sql.ast.join.unrecognised");
    }
  }

  for (const op of SET_OPS) {
    if (node[op]) walkSelect(node[op], out, bound);
  }

  for (const q of node.queries || []) {
    walkSelect(q, out, bound);
  }

  return out;
};

/**
 * The table ids one parsed statement reads, in encounter order.
 *
 * Takes an AST rather than a string, so the api can derive a view's `tables[]`
 * without alasql's evaluator and the browser can reuse the parse it already has.
 */
export const tableIdsOf = (statement) => [...walkSelect(statement, new Set(), new Set())];

export default { tableIdsOf };
